import type { AgentSkill } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { Tabs } from "@design-system/Tabs/Tabs";
import type { AgentSkillPanelTab } from "./agentSkillChat";
import { AgentSkillChatTab } from "./AgentSkillChatTab";
import { AgentSkillDetailsTab } from "./AgentSkillDetailsTab";
import { AgentSkillVersionsTab } from "./AgentSkillVersionsTab";
import type { useAgentSkillLifecycle } from "./useAgentSkillLifecycle";
import styles from "./AgentSkills.module.scss";

const TABS: Array<{ value: AgentSkillPanelTab; label: string }> = [
    { value: "chat", label: "Chat" },
    { value: "details", label: "Details" },
    { value: "versions", label: "Versions" },
];

interface Props {
    skill: AgentSkill;
    tab: AgentSkillPanelTab;
    onTabChange: (tab: AgentSkillPanelTab) => void;
    canManage: boolean;
    clientName: string | null;
    restore: ReturnType<typeof useAgentSkillLifecycle>["restore"];
    onUseDraft: (body: string) => void;
    onMoved: (skillId: string) => void;
}

export const AgentSkillSidePanel = ({
    skill,
    tab,
    onTabChange,
    canManage,
    clientName,
    restore,
    onUseDraft,
    onMoved,
}: Props) => (
    <Tabs.Root
        value={tab}
        onValueChange={(value) => {
            const next = TABS.find((candidate) => candidate.value === value);
            if (next) {
                onTabChange(next.value);
            }
        }}
        className={styles.paneTabs}
    >
        <Tabs.List className={styles.paneTabList} hasIndicator={false}>
            {TABS.map(({ value, label }) => (
                <Tabs.Tab key={value} value={value} className={styles.paneTab}>
                    {label}
                </Tabs.Tab>
            ))}
        </Tabs.List>
        <Tabs.Panel value="chat" className={styles.paneChatPanel} keepMounted>
            <AgentSkillChatTab
                skill={skill}
                canManage={canManage}
                onUseDraft={onUseDraft}
                onMoved={onMoved}
            />
        </Tabs.Panel>
        <Tabs.Panel value="details" className={styles.panePanel} keepMounted>
            <AgentSkillDetailsTab skill={skill} clientName={clientName} />
        </Tabs.Panel>
        <Tabs.Panel value="versions" className={styles.panePanel}>
            <AgentSkillVersionsTab
                skill={skill}
                canManage={canManage}
                restore={restore}
                onMoved={onMoved}
            />
        </Tabs.Panel>
    </Tabs.Root>
);
